import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import {
  VaSidenav,
  VaSidenavItem,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  convertUrlPathToPageConfigPath,
  createFormPageList,
  createPageList,
  getActiveExpandedPages,
  getUrlPathIndex,
  isActivePage,
} from '@department-of-veterans-affairs/platform-forms-system/helpers';
import { setData } from 'platform/forms-system/src/js/actions';

import formConfig from '../config/form';

// Styling for a chapter the Veteran hasn't reached yet: looks like a nav item
// but isn't a link (matches the live 526 side nav).
const DISABLED_STYLE =
  'vads-u-margin--0 vads-u-padding-y--1 vads-u-padding-left--2 vads-u-padding-right--0p5 vads-u-color--gray-medium vads-u-border-color--gray-lightest vads-u-border-bottom--1px';

// Stable empty-object identity so `formData` keeps the same reference across
// renders when the store has no form data yet.
const EMPTY_FORM_DATA = {};

const chapterTitle = chapter =>
  typeof chapter.title === 'function' ? chapter.title() : chapter.title;

// Build the visible chapter steps from the form config, evaluating each
// chapter's `depends` against the current form data (same helpers the platform
// FormNav uses) so conditional chapters/pages are reflected in the side nav.
// `idx` is the chapter's position in the config, used to gate future chapters.
export const buildSteps = (formData, pathname) => {
  const pageList = createPageList(formConfig, createFormPageList(formConfig));
  const eligiblePages = getActiveExpandedPages(pageList, formData);

  // Resolve the current chapter from the pathname:
  //  1. an active (expanded) page with this exact path
  //  2. else the longest active page path this pathname extends
  //  3. else the configured page whose path equals the pathname once the
  //     platform's convertUrlPathToPageConfigPath has turned the numeric
  //     segment back into `:index` (the same lookup the platform's
  //     activeFormPageContext does on ROUTE_CHANGE). Covers list & loop item
  //     pages the platform hasn't expanded for this form data, e.g. adding an
  //     item at an index past the saved array, which is common now that Step 4
  //     item pages are flat slugs with the index last
  //     (e.g. supporting-evidence/va-military-facility-name/:index) and so no
  //     longer extend the loop's intro or summary path.
  //
  //     Page paths are unique across the form config, so the whole-path match
  //     identifies exactly one page and its chapter; no chapter scoping is
  //     needed. The page's `depends` is honored via the platform's
  //     isActivePage, with the index taken from the URL, so an inactive page
  //     (e.g. the traumatic-events item page without the online 0781) yields
  //     no current chapter rather than one the Veteran can't be on.
  const configPath = `${
    formConfig.urlPrefix || ''
  }${convertUrlPathToPageConfigPath(pathname)}`;
  const configPage = pageList.find(page => page.path === configPath);
  const configChapterKey =
    configPage &&
    isActivePage({ ...configPage, index: getUrlPathIndex(pathname) }, formData)
      ? configPage.chapterKey
      : '';
  const currentChapterKey =
    eligiblePages.find(page => page.path === pathname)?.chapterKey ||
    eligiblePages
      .filter(page => page.path && pathname.startsWith(page.path))
      .sort((a, b) => b.path.length - a.path.length)[0]?.chapterKey ||
    configChapterKey;

  const chapterSteps = Object.keys(formConfig.chapters || {})
    .map((key, idx) => {
      const chapter = formConfig.chapters[key];
      const firstPage = eligiblePages.find(page => page.chapterKey === key);
      return firstPage
        ? {
            key,
            idx,
            label: chapterTitle(chapter) || key,
            path: firstPage.path,
            current: key === currentChapterKey,
          }
        : null;
    })
    .filter(Boolean);

  // Review & submit is the final step; it unlocks once the Veteran reaches the
  // last visible chapter (shares that chapter's index for the nav's gating).
  const lastChapterIdx = chapterSteps.length
    ? chapterSteps[chapterSteps.length - 1].idx
    : 0;
  chapterSteps.push({
    key: 'review-and-submit',
    idx: lastChapterIdx,
    label: 'Review and submit',
    path: '/review-and-submit',
    current: pathname.endsWith('review-and-submit'),
  });

  return chapterSteps;
};

function AllClaimsV2SideNav({ pathname, router }) {
  const dispatch = useDispatch();
  const formData = useSelector(state => state.form?.data ?? EMPTY_FORM_DATA);
  const steps = buildSteps(formData, pathname);
  const maxChapterIndex = formData['view:sideNavChapterIndex'] || 0;
  const currentChapterIndex = steps.find(step => step.current)?.idx ?? -1;

  // Once the Veteran reaches a new chapter, record it so the side nav unlocks
  // that chapter (and everything before it) but not future ones — matching the
  // live 526 flow where you can't jump ahead to sections you haven't seen. The
  // guard (new index must exceed the stored max) stops this from re-dispatching.
  useEffect(() => {
    if (currentChapterIndex > maxChapterIndex) {
      dispatch(
        setData({
          ...formData,
          'view:sideNavChapterIndex': currentChapterIndex,
        }),
      );
    }
  }, [currentChapterIndex, maxChapterIndex, dispatch, formData]);

  if (!steps.length) {
    return null;
  }

  const handleClick = (e, path) => {
    e.preventDefault();
    if (path && path !== pathname) {
      router.push(path);
    }
  };

  return (
    <div className="all-claims-v2-sidenav-wrapper">
      <VaSidenav
        header="Form steps"
        icon-background-color="vads-color-link"
        icon-name="description"
        mobileHeader="Select a step"
        navAriaLabel="Form steps"
      >
        {steps.map((step, index) => {
          const label = `Step ${index + 1}: ${step.label}`;
          if (step.current) {
            return (
              <VaSidenavItem
                key={step.key}
                label={label}
                href="#"
                current-page
                data-page={step.key}
                onClick={e => handleClick(e, step.path)}
              />
            );
          }
          if (step.idx <= maxChapterIndex) {
            return (
              <VaSidenavItem
                key={step.key}
                label={label}
                href="#"
                data-page={step.key}
                onClick={e => handleClick(e, step.path)}
              />
            );
          }
          // Future chapter — not yet reachable.
          return (
            <p role="listitem" className={DISABLED_STYLE} key={step.key}>
              <span className="sr-only">{`Complete previous section first. ${label}`}</span>
              <span aria-hidden="true">{label}</span>
            </p>
          );
        })}
      </VaSidenav>
    </div>
  );
}

AllClaimsV2SideNav.propTypes = {
  pathname: PropTypes.string,
  router: PropTypes.object,
};

export default withRouter(AllClaimsV2SideNav);
